import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Mail, Key, ArrowRight, Shield, Smartphone, User, AlertCircle, Loader2 } from "lucide-react";

const ForgotPasswordPage = () => {
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState<"email" | "phone" | "username">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [requestData, setRequestData] = useState<{
    username: string;
    timestamp: string;
    identifier: string;
    type: string;
  } | null>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();

  // Auto-detect identifier type
  useEffect(() => {
    if (identifier.includes("@")) {
      setIdentifierType("email");
    } else if (/^\d+$/.test(identifier)) {
      setIdentifierType("phone");
    } else if (identifier.trim().length > 0) {
      setIdentifierType("username");
    }
  }, [identifier]);

  useEffect(() => {
    document.getElementById("identifier")?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setRequestData(null);

    if (!identifier.trim()) {
      return setError("Please enter your email, phone number, or username");
    }

    // Validate based on identifier type
    if (identifierType === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
      return setError("Please enter a valid email address");
    }

    if (identifierType === "phone" && (identifier.length < 10 || identifier.length > 15)) {
      return setError("Please enter a valid phone number (10-15 digits)");
    }

    if (identifierType === "username" && identifier.length < 3) {
      return setError("Username must be at least 3 characters");
    }

    setLoading(true);

    try {
      // Simulate API processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate mock data for the request
      const mockUsername = identifierType === "username" 
        ? identifier 
        : `user_${Math.random().toString(36).substr(2, 8)}`;
      
      const requestInfo = {
        username: mockUsername,
        identifier: identifier,
        type: identifierType,
        timestamp: new Date().toLocaleString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short"
        })
      };

      setRequestData(requestInfo);
      setSuccess(true);

    } catch (err: any) {
      setError(err.message || "Failed to process your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openWhatsAppWithData = () => {
    if (!requestData) return;

    const phoneNumber = "201503323006";
    
    // Create detailed WhatsApp message
    const message = `🔐 **Password Reset Request**\n\n` +
                   `**Account Details:**\n` +
                   `👤 Username: ${requestData.username}\n` +
                   `📧 Email/Phone: ${requestData.identifier}\n` +
                   `📱 Type: ${requestData.type.toUpperCase()}\n` +
                   `📅 Request Time: ${requestData.timestamp}\n\n` +
                   `**Action Required:**\n` +
                   `Please send a temporary password for this account via WhatsApp.\n\n` +
                   `**Instructions for User:**\n` +
                   `1. User will receive temporary password via WhatsApp\n` +
                   `2. User will login with temporary password\n` +
                   `3. User will change password from account settings`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
    
    // Show confirmation message
    setTimeout(() => {
      alert("WhatsApp opened! Please send the request to our support team.\n\nThey will reply with a temporary password that you can use to login.");
    }, 500);
  };

  const getIdentifierIcon = () => {
    switch (identifierType) {
      case "email":
        return <Mail className="w-5 h-5 text-gray-400" />;
      case "phone":
        return <Smartphone className="w-5 h-5 text-gray-400" />;
      case "username":
        return <User className="w-5 h-5 text-gray-400" />;
      default:
        return <Mail className="w-5 h-5 text-gray-400" />;
    }
  };

  const getPlaceholder = () => {
    switch (identifierType) {
      case "email":
        return "you@example.com";
      case "phone":
        return "+201234567890";
      case "username":
        return "your_username";
      default:
        return "Enter email, phone, or username";
    }
  };

  const getIdentifierLabel = () => {
    switch (identifierType) {
      case "email":
        return "Email Address";
      case "phone":
        return "Phone Number";
      case "username":
        return "Username";
      default:
        return "Identifier";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50 px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100/50 to-orange-100/50 -z-10" />
        
        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm w-full p-8 rounded-3xl shadow-2xl space-y-8 border border-white/20">
          {/* Logo/Brand Area */}
          <div className="text-center space-y-3">
            <div className="flex justify-center mb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="space-y-1">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Forgot Password?
              </h1>
              <p className="text-gray-500 text-sm">
                Request a temporary password via WhatsApp support
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 bg-red-50/80 border border-red-200 rounded-xl text-red-600 text-sm backdrop-blur-sm"
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span>{error}</span>
              </div>
            </motion.div>
          )}

          {/* Instructions Banner */}
          {!success && (
            <div className="p-4 bg-blue-50/80 border border-blue-200 rounded-xl backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                  <span className="text-blue-500 text-sm">ℹ️</span>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-800 mb-1">How it works:</h3>
                  <ol className="text-blue-700 text-sm space-y-1 pl-4 list-decimal">
                    <li>Enter your account details below</li>
                    <li>We'll prepare a request message for WhatsApp</li>
                    <li>Our support team will send you a temporary password</li>
                    <li>Use the temporary password to login</li>
                    <li>Change your password from account settings</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Password Reset Flow */}
          {!success ? (
            // Step 1: Enter Account Details
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="identifier" className="block text-sm font-semibold text-gray-700">
                  {getIdentifierLabel()}
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    ({identifierType === "email" ? "Email" : identifierType === "phone" ? "Phone" : "Username"})
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {getIdentifierIcon()}
                  </div>
                  <input
                    id="identifier"
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value.trim())}
                    disabled={loading}
                    className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:opacity-50 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                    placeholder={getPlaceholder()}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the email, phone number, or username associated with your account
                </p>
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-amber-600 hover:to-orange-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Preparing Request...</span>
                  </>
                ) : (
                  <>
                    <span>Continue to WhatsApp Request</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </motion.button>
            </form>
          ) : (
            // Step 2: WhatsApp Request
            <div className="space-y-6">
              {/* Request Summary */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Request Prepared Successfully
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-amber-700">Account Type:</span>
                    <span className="font-medium capitalize">{requestData?.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Identifier:</span>
                    <span className="font-medium">{requestData?.identifier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Request ID:</span>
                    <span className="font-mono font-medium">{requestData?.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Request Time:</span>
                    <span className="font-medium">{requestData?.timestamp}</span>
                  </div>
                </div>
              </div>

              {/* Next Steps Instructions */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <h4 className="font-semibold text-green-800 mb-2">Next Steps:</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-green-600 text-sm">1</span>
                    </div>
                    <p className="text-green-700 text-sm">
                      Click the WhatsApp button below to send your request to our support team
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-green-600 text-sm">2</span>
                    </div>
                    <p className="text-green-700 text-sm">
                      Our team will reply with a <strong>temporary password</strong> via WhatsApp
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-green-600 text-sm">3</span>
                    </div>
                    <p className="text-green-700 text-sm">
                      Use the temporary password to <Link to="/login" className="text-green-800 font-semibold underline">login</Link>
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-green-600 text-sm">4</span>
                    </div>
                    <p className="text-green-700 text-sm">
                      Change your password from your account settings after login
                    </p>
                  </div>
                </div>
              </div>

              {/* WhatsApp Button */}
              <motion.button
                onClick={openWhatsAppWithData}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-center gap-3 bg-[#25D366] text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-[#128C7E] transition-all duration-300 group"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <span className="text-lg">📱</span>
                </div>
                <span>Send Request via WhatsApp</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>

              {/* Alternative Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setSuccess(false);
                    setRequestData(null);
                    setIdentifier("");
                  }}
                  className="flex items-center justify-center gap-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors p-3 rounded-lg border border-gray-200"
                >
                  <span>Edit Request</span>
                </button>
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors p-3 rounded-lg border border-amber-200"
                >
                  <span>Back to Login</span>
                </Link>
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="text-center space-y-3">
            <div className="text-xs text-gray-500">
              <p>Support available via WhatsApp 24/7</p>
            </div>
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                ← Back to Login
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                to="/register"
                className="text-sm text-amber-600 hover:text-amber-700 transition-colors"
              >
                Create New Account →
              </Link>
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-amber-100 rounded-full"></div>
                <div className="absolute top-0 left-0 w-12 h-12 border-4 border-amber-500 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <div className="text-center">
                <p className="text-gray-800 font-semibold mb-1">Preparing your request</p>
                <p className="text-gray-500 text-sm">Getting everything ready for WhatsApp...</p>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;